import {
  Button,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import {
  type DashboardPreferenceKey,
  useUpdateDashboardPreference,
} from "@/lib/data/react-query";
import { SlidersHorizontal } from "lucide-react";
import { useId } from "react";

const dashboardPreferenceItems = [
  { key: "dashboardNotifications", label: "Notifications" },
  { key: "dashboardTasks", label: "Tasks" },
  {
    key: "dashboardMyApproachingStatuteOfLimitationMatters",
    label: "My statutes",
  },
  { key: "dashboardFavoriteMatters", label: "Pinned matters" },
  { key: "dashboardMostRecentMatters", label: "Most recent matters" },
] as const satisfies readonly { key: DashboardPreferenceKey; label: string }[];

export function DashboardCustomize({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { currentUser } = useUser();
  const updatePreference = useUpdateDashboardPreference();
  const id = useId();

  return (
    <Popover modal open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="shrink-0">
          <SlidersHorizontal className="h-4 w-4" />
          Customize
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-96 max-w-[calc(100vw-2rem)] p-0"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-description`}
      >
        <div className="border-b px-5 py-4">
          <h2 id={`${id}-title`} className="text-base font-semibold">
            Customize dashboard
          </h2>
          <p
            id={`${id}-description`}
            className="text-muted-foreground mt-1 text-sm"
          >
            Show or hide sections. Changes apply right away.
          </p>
        </div>
        <div className="space-y-4 px-5 py-4">
          {dashboardPreferenceItems.map((item) => (
            <div
              key={item.key}
              className="flex items-center justify-between gap-4"
            >
              <Label
                htmlFor={`${id}-${item.key}`}
                className="text-foreground text-sm font-medium"
              >
                {item.label}
              </Label>
              <Switch
                id={`${id}-${item.key}`}
                checked={Boolean(currentUser[item.key])}
                disabled={updatePreference.isPending}
                onCheckedChange={(checked) => {
                  updatePreference.mutate({
                    id: currentUser.id,
                    dateModified: currentUser.dateModified,
                    key: item.key,
                    checked,
                  });
                }}
              />
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
